import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { Box, Grid, Typography, useTheme } from "@mui/material";
import { Formik, Form } from "formik";
import PropTypes from "prop-types";
import FieldRenderer from "./FieldRenderer";
import generateValidationSchema from "./generateValidationSchema";
import ButtonComponent from "./ButtonComponent";
import {
  useFetchDropdownOptionsQuery,
  useFetchChildDropdownOptionsQuery,
} from "@/api/services/dynamicForm/DynamicFormApi";
import WithLoadingError from "@/hoc/forms/WithLoadingError";
import { ArrowBackIosIcon } from "@/icons";
import { useNavigate } from "react-router-dom";

const DynamicForm = ({
  formData = {},
  setFormData = () => {},
  handleSubmit = () => {},
  handleFieldChange = () => {},
  handleButtonClick = () => {},
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const fieldRefs = useRef({}); // Store field values without causing re-renders
  const dropdownCacheRef = useRef({}); // Store cached dropdown options

  const initialValues = formData?.data?.initialValue || {};

  const memoizedFormFields = useMemo(() => formData?.data || [], [formData]);
  const formFieldsKey = JSON.stringify(memoizedFormFields?.formFields || []);

  const [dropdownOptions, setDropdownOptions] = useState({});
  const [currentChildField, setCurrentChildField] = useState(null);

  const shouldSkip = useMemo(() => {
    return !!dropdownCacheRef.current[formFieldsKey];
  }, [formFieldsKey]);

  const {
    data: fetchedDropdownOptions = {},
    isLoading,
    error,
  } = useFetchDropdownOptionsQuery(memoizedFormFields?.formFields, {
    skip: shouldSkip,
  });

  const { data: fetchedChildDropdownOptions } =
    useFetchChildDropdownOptionsQuery(
      currentChildField ? { ...currentChildField } : null,
      { skip: !currentChildField }
    );

  // console.log("fetchedChildDropdownOptions", fetchedChildDropdownOptions);

  useEffect(() => {
    if (
      fetchedDropdownOptions &&
      Object.keys(fetchedDropdownOptions).length > 0
    ) {
      // Check if data is already in cache to avoid unnecessary updates
      if (
        JSON.stringify(dropdownCacheRef.current) !==
        JSON.stringify(fetchedDropdownOptions)
      ) {
        dropdownCacheRef.current = fetchedDropdownOptions; // Update cache
        setDropdownOptions((prev) => ({
          ...prev,
          ...fetchedDropdownOptions,
        }));
      }
    }
  }, [fetchedDropdownOptions]);

  // Handle child dropdown options
  useEffect(() => {
    if (
      !fetchedChildDropdownOptions?.status ||
      !Array.isArray(fetchedChildDropdownOptions.data) ||
      !currentChildField
    ) {
      return;
    }

    setDropdownOptions((prev) => {
      const updatedOptions = { ...prev };
      memoizedFormFields.formFields.forEach((parentField) => {
        if (
          !parentField.dependencies ||
          parentField.dependencies.length === 0
        ) {
          return;
        }

        parentField.dependencies.forEach((dep) => {
          if (
            String(parentField.fieldId) === String(currentChildField.fieldId)
          ) {
            updatedOptions[dep.fieldId] = fetchedChildDropdownOptions.data;
          }
        });
      });

      return updatedOptions;
    });
  }, [fetchedChildDropdownOptions, currentChildField, memoizedFormFields]);

  // Field Change Handling
  const handleFieldChangeInternal = useCallback(
    (event, fieldId, setFieldValue, setErrors) => {
      const value = event.target.value;
      fieldRefs.current[fieldId] = value; // Update local reference without triggering re-renders
      updateDependenciesAndFormData(fieldId, value, setFieldValue, setErrors);
      if (handleFieldChange) handleFieldChange(event, fieldId);
    },
    [handleFieldChange]
  );

  const fieldStateRef = useRef({}); // Store dependency updates without triggering re-renders

  console.log("fieldStateRef", fieldStateRef);

  const updateDependenciesAndFormData = useCallback(
    (changedFieldId, changedValue, setFieldValue, setErrors) => {
      setFormData((prev) => {
        const changedField = prev.data.formFields.find(
          (f) => f.fieldId === changedFieldId
        );

        if (!changedField || !changedField.dependencies) return prev;

        let shouldUpdateState = false; // Track whether we actually need a state update
        const updatedFields = prev.data.formFields.map((field) => {
          const dependencies = changedField.dependencies.filter(
            (dep) => dep.fieldId === field.fieldId
          );

          if (!dependencies) return field;

          let updatedField = { ...field };

          const getNestedValue = (obj, path) => {
            return path
              .split(".")
              .reduce(
                (acc, key) =>
                  acc && acc[key] !== undefined ? acc[key] : undefined,
                obj
              );
          };

          dependencies.forEach((dependency) => {
            switch (dependency.condType) {
              case "Fetch":
                if (field.optionsReferenceLink) {
                  setCurrentChildField({
                    optionsReferenceLink: field.optionsReferenceLink,
                    param: dependency.param,
                    fieldId: changedFieldId,
                    changedValue,
                  });
                }
                break;

              case "Change":
                const newShow = changedValue !== dependency.expectedValue;
                const newDisabled = changedValue === dependency.expectedValue;

                if (
                  fieldStateRef.current[field.fieldId]?.show !== newShow ||
                  fieldStateRef.current[field.fieldId]?.disabled !== newDisabled
                ) {
                  fieldStateRef.current[field.fieldId] = {
                    show: newShow,
                    disabled: newDisabled,
                  };
                  shouldUpdateState = true; // Only update state if values change
                }
                break;

              case "Populate":
                const selectedOption =
                  changedField?.optionsReference?.options.find(
                    (opt) => opt.value === changedValue
                  );

                if (selectedOption) {
                  let shouldSetValue = false;
                  let newValue = "";

                  if (dependency.matchOptionFieldId) {
                    const matchFieldValue =
                      selectedOption[dependency.matchOptionFieldId];

                    shouldSetValue =
                      dependency.setValue &&
                      matchFieldValue == dependency.expectedValue;

                    if (matchFieldValue == dependency.expectedValue) {
                      const currentFieldState =
                        fieldStateRef.current[field.fieldId] || {};

                      console.log("currentFieldState", currentFieldState);

                      const shouldUpdate =
                        currentFieldState.show !== dependency.show ||
                        currentFieldState.disabled !== dependency.disabled;

                      console.log("shouldUpdate", shouldSetValue);

                      if (shouldUpdate) {
                        fieldStateRef.current[field.fieldId] = {
                          show: dependency.show,
                          disabled: dependency.disabled,
                        };
                      }
                    }
                  }

                  if (shouldSetValue) {
                    newValue =
                      dependency.value ||
                      getNestedValue(
                        selectedOption,
                        dependency.optionFieldId
                      ) ||
                      "";
                  }

                  if (newValue !== "") {
                    fieldRefs.current[field.fieldId] = newValue;
                    setFieldValue(field.fieldId, newValue, false);
                    // 🚀 Reset validation error
                    // setErrors((prevErrors) => ({
                    //   ...prevErrors,
                    //   [field.fieldId]: undefined, // Clear the error for this field
                    // }));
                  }
                }

                break;
              default:
                break;
            }
          });

          // Apply the field state update only if it changed
          if (fieldStateRef.current[field.fieldId]) {
            updatedField = {
              ...updatedField,
              ...fieldStateRef.current[field.fieldId],
            };
          }

          return updatedField;
        });

        console.log("updatedFields", updatedFields);

        // Only update state if something actually changed
        return shouldUpdateState
          ? { ...prev, data: { ...prev.data, formFields: updatedFields } }
          : prev;
      });
    },
    [setFormData]
  );

  const validationSchema = generateValidationSchema(
    Array.isArray(memoizedFormFields?.formFields)
      ? memoizedFormFields.formFields
      : []
  );

  return (
    <WithLoadingError isLoading={isLoading} error={error} data={formData}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, actions) => {
          handleSubmit(values, actions);
        }}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          isSubmitting,
          setErrors,
        }) => (
          <Form>
            <Grid container alignItems="center" spacing={1}>
              <Grid item>
                <Box
                  sx={{
                    borderRadius: "100%",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "35px",
                    height: "35px",
                    position: "relative",
                    top: "-4px",
                    transition:".5s",
                    "& svg": {
                      fontSize: "15px",
                      position: "relative",
                      left: "3px",
                      top: "1px",
                    },
                    "&:hover": {
                      backgroundColor: (theme) => theme.palette.primary.main,
                    color: (theme) => theme.palette.primary.contrastText,
                    },
                  }}
                  onClick={() => navigate(-1)}
                >
                  <ArrowBackIosIcon
                    sx={{
                      cursor: "pointer",
                      fontWeight: "bold",
                      fontSize: "1.5rem",
                    }}
                  />
                </Box>
              </Grid>
              <Grid item>
                <Typography
                  variant="h5"
                  component="h1"
                  fontWeight="bold"
                  gutterBottom
                >
                  {formData?.data?.formName || "Default Form Title"}
                </Typography>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              {memoizedFormFields?.formFields?.map((field) => (
                <Grid
                  item
                  key={field.fieldId}
                  {...field.position}
                  sx={{ display: field.show ? "block" : "none" }}
                >
                  <FieldRenderer
                    field={field}
                    value={fieldRefs.current[field.fieldId] || ""}
                    setFieldValue={setFieldValue}
                    errors={errors}
                    touched={touched}
                    dropdownOptions={dropdownOptions}
                    handleFieldChange={(event) =>
                      handleFieldChangeInternal(
                        event,
                        field.fieldId,
                        setFieldValue,
                        setErrors
                      )
                    }
                  />
                </Grid>
              ))}
            </Grid>

            <Grid
              sx={{
                display: "flex",
                mt: 2,
                justifyContent: "flex-end",
              }}
            >
              {memoizedFormFields.buttons.map(
                (field) =>
                  field.type === "button" && (
                    <ButtonComponent
                      key={field.fieldId}
                      field={field}
                      handleButtonClick={handleButtonClick}
                      isSubmitting={isSubmitting}
                    />
                  )
              )}
            </Grid>
          </Form>
        )}
      </Formik>
    </WithLoadingError>
  );
};

DynamicForm.propTypes = {
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleFieldChange: PropTypes.func,
  handleButtonClick: PropTypes.func.isRequired,
};

export default DynamicForm;
